import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleOAuthCallback } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        console.log('[OAuth] Processing callback, hash:', hash);
        
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          throw new Error('No session_id found in URL');
        }

        console.log('[OAuth] Exchanging session_id...');
        
        // Exchange session_id for our session
        const userData = await handleOAuthCallback(sessionId);
        
        console.log('[OAuth] Session exchanged successfully');

        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);

        // Navigate based on user state
        if (userData.must_change_password) {
          navigate('/change-password', { replace: true });
        } else if (userData.has_workspace || userData.workspace_id) {
          navigate('/dashboard', { replace: true, state: { user: userData } });
        } else {
          navigate('/onboarding', { replace: true, state: { user: userData } });
        }
      } catch (err) {
        console.error('[OAuth] Callback error:', err);
        
        let errorMessage = 'Authentication failed';
        let details = err.message;
        
        if (err.response) {
          // API returned an error response
          errorMessage = err.response.data?.detail || 'Server error';
          details = `Status: ${err.response.status}`;
        } else if (err.request) {
          // Request was made but no response received
          errorMessage = 'Network error';
          details = 'Could not connect to authentication server. Please check your connection.';
        }
        
        setError(errorMessage);
        setErrorDetails(details);
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    processCallback();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md p-6">
          <div className="mb-4 text-destructive">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-xl font-semibold text-destructive mb-2">Erreur d'authentification</p>
          <p className="text-muted-foreground mb-1">{error}</p>
          {errorDetails && (
            <p className="text-sm text-muted-foreground/70 mb-4">{errorDetails}</p>
          )}
          <p className="text-sm text-muted-foreground">Redirection vers la page de connexion...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Connexion en cours...</p>
      </div>
    </div>
  );
}
